import { css } from 'styled-components';
import { MoviePortraitImage, StyledSVG } from 'style/styled-components/reusable.css';
import { styled } from 'components/ThemeConfigConnector/ThemeConfigConnnector';
import { fontBold } from 'style/styled-components/cssGlobalVariables';
import { ButtonPlayOutline } from 'components/Buttons/ButtonPlayOutline/ButtonPlayOutline';
import { BREAKPOINT_ORDER, Breakpoints, MOBILE_MQ, MQ, MQ_NOT_TOUCH, MQ_TOUCH } from 'style/styled-components/cssMediaQueries';
import { hexToRgba } from 'utils/fnColors';
import { CardContext } from 'types/Common';
import { CardDimensions, CardVariantSpec, GridWidths } from './cardVariants';

export const animationType = 'ease-in-out';
export const animationDuration = '150ms';
export const animationDelay = '150ms';

const cardVariables = (dimensions: Partial<CardDimensions>) => {
    const height = dimensions.cardHeight ?? dimensions.imageHeight;
    const expandedHeight = dimensions.expandedCardHeight ?? height;

    return css`
        ${dimensions.width !== undefined ? `--card-width: ${dimensions.width};` : ''}
        ${dimensions.maxWidth !== undefined ? `--card-max-width: ${dimensions.maxWidth};` : ''}
        ${dimensions.scrollMarginLeft !== undefined ? `--card-scroll-margin-left: ${dimensions.scrollMarginLeft};` : ''}
        ${height !== undefined ? `--card-height: ${height};` : ''}
        ${expandedHeight !== undefined ? `--card-expanded-height: ${expandedHeight};` : ''}
        ${dimensions.infoHeight !== undefined ? `--card-info-height: ${dimensions.infoHeight};` : ''}
        ${dimensions.insetPortraitWidth !== undefined ? `--card-inset-portrait-width: ${dimensions.insetPortraitWidth};` : ''}
        ${dimensions.imageAspectRatio !== undefined ? `--card-image-aspect-ratio: ${dimensions.imageAspectRatio};` : ''}
        ${dimensions.imageHeight !== undefined
            ? `--card-image-height: ${dimensions.imageHeight};
               --card-image-hover-height: calc(${dimensions.imageHeight} + 4px);`
            : ''}
        --card-image-hover-width: calc(100% + 4px);
        --card-description-offset: calc(100% + 12px);
    `;
};

export const cardVariantCss = (spec: CardVariantSpec) => css`
    ${cardVariables(spec)}

    width: var(--card-width);
    max-width: var(--card-max-width);
    ${spec.aspectRatio !== undefined
        ? `aspect-ratio: ${spec.aspectRatio};
    height: auto;`
        : 'height: var(--card-height);'}
    ${spec.scrollMarginLeft !== undefined ? 'scroll-margin-left: var(--card-scroll-margin-left);' : ''}

    ${BREAKPOINT_ORDER.map((breakpoint) => {
        const dimensions = spec.responsive?.[breakpoint];

        if (!dimensions) {
            return '';
        }

        return css`
            ${MQ(Breakpoints[breakpoint])} {
                ${cardVariables(dimensions)}

                ${spec.aspectRatio !== undefined ? '' : 'height: var(--card-height);'}
            }
        `;
    })}
`;

export const ImageWrapper = styled.div`
    width: 100%;
    aspect-ratio: var(--card-image-aspect-ratio, auto);
    height: var(--card-image-height, auto);
    position: relative;
    overflow: hidden;
    border-radius: 5px;
    border: var(--card-image-border, none);
    opacity: 0.9;

    img {
        width: 100%;
        height: 100%;
    }
`;

export const StyledButtonPlay = styled(ButtonPlayOutline)`
    opacity: 0;
    pointer-events: none;
`;

export const InfoWrapper = styled.div<{ hasDescription?: boolean; context?: CardContext }>`
    width: 100%;

    ${(props) =>
        props.context === CardContext.COLLECTION
            ? css`
                  height: 44px;
                  padding: 10px 0px;
              `
            : css`
                  position: absolute;
                  height: var(--card-info-height);
                  padding: 4px 8px 10px 0px;

                  ${props.hasDescription
                      ? css`
                            ${MQ(Breakpoints.xs)} {
                                top: 0;
                                left: var(--card-description-offset);
                            }
                        `
                      : css`
                            top: var(--card-image-height);
                        `}

                  ${MQ(Breakpoints.s)} {
                      top: var(--card-image-height);
                      left: 0;
                  }
              `}
`;

export const DescriptionWrapper = styled.p`
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    text-overflow: ellipsis;
    overflow: hidden;
    width: 90vw;
    font-size: 12px;
    line-height: 16px;
    clear: both;
    white-space: normal;
    color: ${(props) => hexToRgba(props.theme.brandedConfig.high_contrast, 0.7)};

    ${MQ(Breakpoints.xs)} {
        width: 50vw;
    }

    ${MQ(Breakpoints.s)} {
        width: var(--card-width);
    }
`;

export const Title = styled.p<{ context?: CardContext }>`
    color: ${(props) => props.theme.brandedConfig.high_contrast};
    font-size: 16px;
    line-height: 24px;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
    font-family: ${fontBold};

    ${(props) =>
        props.context === CardContext.COLLECTION
            ? css``
            : css`
                  float: left;
                  width: 90vw;

                  ${MQ(Breakpoints.xs)} {
                      width: 50vw;
                  }

                  ${MQ(Breakpoints.s)} {
                      width: 100%;
                  }
              `}

    ${StyledSVG} {
        margin-right: 4px;
    }
`;

export const MetaData = styled.p<{ hasDescription?: boolean; context?: CardContext }>`
    color: ${(props) => hexToRgba(props.theme.brandedConfig.high_contrast, 0.7)};
    font-size: 12px;
    line-height: 16px;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
    clear: both;
    margin-top: ${(props) => (props?.hasDescription ? '8px' : '0px')};

    ${(props) =>
        props.context === CardContext.COLLECTION
            ? css`
                  opacity: 0;
                  transition: opacity 300ms ${animationType} 0ms;

                  @media ${MOBILE_MQ} {
                      display: none;
                  }
              `
            : css`
                  opacity: 1;

                  ${MQ_TOUCH} {
                      display: none;
                  }
              `}
`;

export const CardContainer = styled.div<{ defaultRenderSecondLine?: boolean; hasDescription?: boolean }>`
    & > a,
    & > div:first-child {
        width: 100% !important;
        height: var(--card-image-height);
    }

    ${MetaData} {
        ${MQ(Breakpoints.m)} {
            transition: opacity 300ms ${animationType} 0ms;
            opacity: ${(props) => (props.defaultRenderSecondLine || props.hasDescription ? 1 : 0)};
        }
    }

    ${MQ_NOT_TOUCH} {
        &:hover ${MetaData} {
            opacity: 1;
        }
    }
`;

export const CardFrame = styled.div<{ defaultRenderSecondLine?: boolean }>`
    width: 100%;
    height: ${(props) => (props.defaultRenderSecondLine ? 'var(--card-expanded-height)' : 'var(--card-height)')};
    position: absolute;
    left: 0;
    top: 0;
    cursor: pointer;
    outline: 2px solid transparent;
    box-shadow: 0 4px 8px ${(props) => hexToRgba(props.theme.brandedConfig.black_color, 0.05)};

    ${StyledButtonPlay} {
        transition: opacity 300ms ${animationType} 0ms;
    }

    ${MQ_NOT_TOUCH} {
        &:hover {
            ${StyledButtonPlay} {
                transition: opacity 300ms ${animationType} 200ms;
                pointer-events: all;
                opacity: 1;
            }
        }

        ${(props) =>
            !props.defaultRenderSecondLine
                ? css`
                      &:hover {
                          ${ImageWrapper} {
                              border: 2px solid transparent;
                              outline: 2px solid var(--color-dark-body);
                              opacity: 1;
                              margin: -2px 0 0 -2px;

                              ${MQ(Breakpoints.s)} {
                                  width: var(--card-image-hover-width) !important;
                                  height: var(--card-image-hover-height) !important;
                              }
                          }
                      }
                  `
                : css``};
    }
`;

export const gridWidthsCss = (gridWidths: GridWidths) => css`
    width: ${gridWidths.base};

    ${BREAKPOINT_ORDER.map((breakpoint) =>
        gridWidths[breakpoint] !== undefined
            ? css`
                  ${MQ(Breakpoints[breakpoint])} {
                      width: ${gridWidths[breakpoint]};
                  }
              `
            : ''
    )}
`;

export const CardGridTile = styled.div<{ imageAspectRatio?: string }>`
    ${(props) => (props.imageAspectRatio !== undefined ? `--card-image-aspect-ratio: ${props.imageAspectRatio};` : '')}

    width: 100%;
    position: relative;
    cursor: pointer;
    box-shadow: 0 4px 8px ${(props) => hexToRgba(props.theme.brandedConfig.black_color, 0.05)};
`;

export const CardGridItem = styled.div<{ gridWidths: GridWidths; defaultRenderSecondLine?: boolean; withTileFocusOutline?: boolean }>`
    ${StyledButtonPlay} {
        transition: opacity 300ms ${animationType} 0ms;
    }

    display: block;
    float: left;
    position: relative;
    z-index: 1;
    min-width: 0;
    transition-property: z-index;
    transition-delay: ${animationDelay};
    padding: 8px 8px;

    @media ${MOBILE_MQ} {
        padding: 0;
    }

    ${(props) => gridWidthsCss(props.gridWidths)}

    ${MQ_NOT_TOUCH} {
        &:hover {
            z-index: 2;

            ${StyledButtonPlay} {
                transition: opacity 300ms ${animationType} 200ms;
                opacity: 1;
                pointer-events: all;
            }

            ${MetaData} {
                transition: opacity 300ms ${animationType} 200ms;
                opacity: 1;
            }

            ${ImageWrapper} {
                outline: var(--focus-outline-solid);
                outline-offset: 2px;
                opacity: 1;
            }
        }
    }

    ${(props) =>
        props.withTileFocusOutline
            ? css`
                  &:has(*:focus-visible) ${CardGridTile} {
                      outline: var(--focus-outline);
                      border-radius: 5px;
                  }
              `
            : css``}

    ${(props) =>
        props.defaultRenderSecondLine
            ? css`
                  margin-bottom: 16px;

                  ${InfoWrapper} {
                      height: 56px;
                      padding: 8px 8px;
                  }

                  ${MetaData} {
                      transition: opacity 300ms ${animationType} 200ms;
                      opacity: 1;
                  }
              `
            : css``};
`;

export const MediaBackdrop = styled.div<{ isBackgroundBlur: boolean }>`
    width: 100%;
    height: 100%;
    background: #232438;

    ${(props) =>
        props.isBackgroundBlur
            ? css`
                  filter: blur(16px);
              `
            : ''};
`;

export const InsetPortraitImage = styled(MoviePortraitImage)`
    width: var(--card-inset-portrait-width, 120px);
`;

export const Favorite = styled.div`
    border-radius: 4px;
    background: rgba(19, 19, 32, 0.7);
    width: 20px;
    height: 20px;
    position: absolute;
    top: 8px;
    left: 8px;
    z-index: 2;

    ${StyledSVG} {
        height: 12px;
        margin-left: 1px;
        margin-top: -5px;
    }
`;
